/* eslint-disable no-shadow */
import get from 'lodash.get';
import orderBy from 'lodash.orderby';
import sumBy from 'lodash.sumby';

// Local Types
import {
    PersonFullNames,
    PersonEmail,
    EntityName,
    EntityId,
    Entity,
    EntitiesKeyedById,
    EntityEntityTypeId,
    EntityDefaultVarietyId
} from './types';

// Remove unecessary fields from Entity before sending to API
export const formatEntityPayload = ({
    removals,
    plantingDate,
    numberOfTrees,
    totalArea,
    ...entity
}: Entity): Entity => {
    let treeDetailPayload;
    if (plantingDate !== null) {
        let formattedRemovals;
        if (removals) {
            formattedRemovals = removals.map(
                // eslint-disable-next-line @typescript-eslint/no-shadow
                ({ date, numberOfTrees, totalArea }) => ({
                    date,
                    numberOfTrees,
                    totalArea
                })
            );
        }

        treeDetailPayload = {
            plantingDate,
            numberOfTrees,
            totalArea,
            removals: formattedRemovals
        };
    }

    return {
        ...entity,
        ...treeDetailPayload
    };
};

export const formatEntityPayloads = (
    entities?: Entity[]
): Entity[] | undefined => {
    return entities && entities.map(formatEntityPayload);
};

export const formatEntity = ({ assignedUsers, ...entity }: Entity): Entity => {
    const inspectionUserEmails: PersonEmail[] = [];
    const timesheetUserEmails: PersonEmail[] = [];

    if (assignedUsers && assignedUsers.length) {
        assignedUsers.forEach(({ email, jobType }) => {
            if (jobType === 'INSPECTION') {
                inspectionUserEmails.push(email);
            }

            if (jobType === 'TIMESHEET') {
                timesheetUserEmails.push(email);
            }
        });
    }

    return {
        ...entity,
        inspectionUserEmails,
        timesheetUserEmails,
        assignedUsers
    };
};

export const formatEntities = (entities: Entity[]): Entity[] => {
    return entities.map((entity) => formatEntity(entity));
};

export const getUsersEmails = (
    users: Record<PersonEmail, any>,
    emails?: PersonEmail[]
): PersonFullNames | undefined => {
    if (!emails || !emails.length) {
        return undefined;
    }

    const fullNames = emails
        .map((email) => get(users, [email, 'fullName']))
        .filter((fullName) => fullName);

    const orderedFullNames = orderBy(fullNames);

    return orderedFullNames.join(', ');
};

interface GetEntityPathResults {
    pathIds: EntityId[];
    pathNames: EntityName[];
    archivedAt?: string | null;
}

export const getEntityPath = (
    entity: Entity,
    entities: EntitiesKeyedById,
    results?: GetEntityPathResults
): GetEntityPathResults => {
    const { parentEntityId, name, id, archivedAt } = entity;

    let nextResults = results;

    if (nextResults && id) {
        nextResults.pathIds.unshift(id);
        nextResults.pathNames.unshift(name);
    } else {
        nextResults = {
            pathIds: [],
            pathNames: [name]
        };
    }

    if (!nextResults.archivedAt) {
        nextResults.archivedAt = archivedAt;
    }

    if (parentEntityId) {
        const parentEntity = get(entities, parentEntityId);
        if (parentEntity) {
            getEntityPath(parentEntity, entities, nextResults);
        }
    }
    return nextResults;
};

interface GetEntityHierarchyResult {
    path: string | null;
    pathNames: string;
    archivedAt?: string | null;
}

export const getEntityHierarchy = (
    entity: Entity,
    entities: EntitiesKeyedById
): GetEntityHierarchyResult => {
    if (!entity) {
        return {
            path: '',
            pathNames: ''
        };
    }
    const entityPath = getEntityPath(entity, entities);
    const { pathIds, pathNames, archivedAt } = entityPath;

    return {
        path: pathIds.length ? `/${pathIds.join('/')}` : null,
        pathNames: pathNames.join(' / '),
        archivedAt
    };
};

interface EntitySummedRemovals {
    sumTotalArea: number;
    sumNumberOfTrees: number;
}

export const getEntitySummedRemovals = (
    removals: Entity['removals']
): EntitySummedRemovals => {
    return {
        sumTotalArea: sumBy(removals, (removal) => removal.totalArea || 0),
        sumNumberOfTrees: sumBy(
            removals,
            (removal) => removal.numberOfTrees || 0
        )
    };
};

export const getEntityVarietyId = (
    entityId: EntityId,
    entities: EntitiesKeyedById,
    varietyEntityTypeId: EntityEntityTypeId
): EntityDefaultVarietyId => {
    let varietyId = null;
    const entityPath = get(entities, [entityId, 'path']);

    // A fully qualified entity path includes the entity itself
    const entityIdsOnPath = entityPath
        ? [...entityPath.split('/').slice(1), entityId.toString()]
        : [];

    const entityIdWithVariety = entityIdsOnPath.find((id) => {
        const entityTypeId = get(entities, [id, 'entityTypeId']);

        return entityTypeId === varietyEntityTypeId;
    });

    if (entityIdWithVariety) {
        varietyId = get(entities, [entityIdWithVariety, 'defaultVarietyId']);
    }

    return varietyId;
};
